import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Alert,
  CardActionArea,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Event as EventIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'motion/react';
import {
  RejectionModalContentProps,
  RejectionReason,
  ScheduleAppointmentPayload,
} from '../../types';
import { PRESET_DURATIONS, useDurationSchedule } from '@features/appointments';
import { useSubjectLanguage } from '@features/user';
import { useTracking } from '../../../../context/TrackingContext';
import { EventType } from '../../../../types/events';
import { DurationSchedulePicker } from '@features/appointments';

// Step definitions for the multi-step flow
type ModalStep = 'initial' | 'edit' | 'confirmation' | 'feedback';

/**
 * Appointment-specific rejection modal content component
 * Provides options specific to appointment suggestion rejections
 */
export const ScheduleAppointmentRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  onAccept,
  isLoading = false,
}) => {
  const { action, patientName } = context;
  const subjectLanguage = useSubjectLanguage();
  const { trackEvent } = useTracking();
  const [currentStep, setCurrentStep] = useState<ModalStep>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
  const rejectionTriggeredRef = useRef(false);

  // Type guard to ensure we have appointment data
  if (action.action_type !== 'schedule_appointment') {
    console.error('ScheduleAppointmentRejection received non-appointment action');
    return null;
  }

  const appointmentData = action.action_payload as ScheduleAppointmentPayload;

  // Memoize the initial values to prevent hook re-initialization on every render
  // TODO: update hook to support initial values as string appointmentData.scheduled_for
  const initialAppointmentDate = useMemo(
    () => new Date(appointmentData.scheduled_for),
    [appointmentData.scheduled_for],
  );

  const initialAppointmentDuration = useMemo(
    () => appointmentData.scheduled_duration,
    [appointmentData.scheduled_duration],
  );

  const durationScheduleHook = useDurationSchedule({
    initialDate: initialAppointmentDate,
    initialDuration: initialAppointmentDuration,
  });

  // Auto-close countdown for confirmation step
  useEffect(() => {
    if (currentStep === 'confirmation' && autoCloseCountdown === null) {
      setAutoCloseCountdown(5);
    }
  }, [currentStep, autoCloseCountdown]);

  useEffect(() => {
    if (autoCloseCountdown !== null && autoCloseCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoCloseCountdown(autoCloseCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoCloseCountdown === 0 && !rejectionTriggeredRef.current) {
      // When countdown reaches 0, trigger rejection
      rejectionTriggeredRef.current = true;

      onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
    }
  }, [autoCloseCountdown, onReject]);

  // Format the appointment details for display
  const formattedAppointmentDate = new Date(appointmentData.scheduled_for).toLocaleDateString();
  const formattedAppointmentTime = new Date(appointmentData.scheduled_for).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleReasonSelect = (reason: RejectionReason) => {
    switch (reason) {
      case RejectionReason.WRONG_SUGGESTION:
        setCurrentStep('edit');
        break;
      case RejectionReason.DONT_CARE_FOR_FEATURE:
        setCurrentStep('confirmation');
        break;
      case RejectionReason.OTHER:
        setCurrentStep('feedback');
        break;
    }
  };

  const handleEditConfirm = () => {
    // Get the updated appointment data from the hook
    const { startDateTime, duration } = durationScheduleHook.getAppointmentDateTime();

    if (startDateTime && duration !== undefined) {
      const updatedAppointment: ScheduleAppointmentPayload = {
        scheduled_for: startDateTime.toISOString(),
        scheduled_duration: duration,
      };

      // Track the edit event before accepting
      trackEvent(EventType.ACTION_EDITED, {
        action_id: action.action_id,
        action_type: action.action_type,
        context: 'rejection_modal',
        original_action_payload: appointmentData,
        updated_action_payload: updatedAppointment,
        encounter_id: action.encounter_id || null,
        patient_id: action.patient?.patient_id || null,
      });

      // Accept the action with edited appointment data
      onAccept?.(action.action_id, updatedAppointment);
    }
  };

  const handleEditReject = () => {
    // Pass rejection for centralized tracking
    onReject(RejectionReason.WRONG_SUGGESTION);
  };

  const handleFeedbackSubmit = () => {
    // Pass rejection with feedback for centralized tracking
    onReject(RejectionReason.OTHER, {
      feedback: feedbackText.trim() || undefined,
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setAutoCloseCountdown(null);
    rejectionTriggeredRef.current = false; // Reset the rejection flag
  };

  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={currentStep}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: { duration: 0.3, ease: 'easeInOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ width: '100%' }}
      >
        {currentStep === 'initial' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Appointment Summary */}
              <Alert severity="info" icon={<EventIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Suggested appointment:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    {patientName && <>{patientName} • </>}
                    {formattedAppointmentDate} at {formattedAppointmentTime}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'primary.main', ml: 0.5 }}
                    >
                      ({appointmentData.scheduled_duration} minutes)
                    </Typography>
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.WRONG_SUGGESTION)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EditIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        This appointment suggestion is incorrect
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {`The suggested date, time, or duration doesn't work for
                        this ${subjectLanguage}.`}
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        I don't want appointment suggestions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off appointment suggestions for all future notes
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => handleReasonSelect(RejectionReason.OTHER)}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from accepting this suggestion
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'edit' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Edit Appointment Details
                </Typography>
              </Box>

              <Alert severity="info">
                <Typography variant="body2">
                  Review and modify the appointment details below. You can then choose to accept the
                  corrected appointment or reject it entirely.
                </Typography>
              </Alert>

              {/* Duration Schedule Picker */}
              <DurationSchedulePicker
                date={durationScheduleHook.date}
                startTime={durationScheduleHook.startTime}
                endTime={durationScheduleHook.endTime}
                selectedDuration={durationScheduleHook.selectedDuration}
                isCustomDuration={durationScheduleHook.isCustomDuration}
                onDateChange={durationScheduleHook.handleDateChange}
                onStartTimeChange={durationScheduleHook.handleStartTimeChange}
                onEndTimeChange={durationScheduleHook.handleEndTimeChange}
                onDurationSelect={durationScheduleHook.handleDurationSelect}
                onCustomDurationSelect={durationScheduleHook.handleCustomDuration}
                slimDurations={PRESET_DURATIONS}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleEditReject} variant="outlined" color="error">
                  Still Reject
                </Button>
                <Button onClick={handleEditConfirm} variant="contained" color="primary">
                  Accept Changes
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Appointment Suggestions Disabled
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive appointment suggestions for future notes. You can
                re-enable this feature anytime in your preferences.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  if (!rejectionTriggeredRef.current) {
                    rejectionTriggeredRef.current = true;

                    // Pass rejection when user clicks "Close"
                    onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'feedback' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve by sharing what prevented you from accepting this appointment
                suggestion.
              </Typography>

              <TextField
                label="Your feedback"
                multiline
                rows={4}
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                placeholder="Please describe why this appointment suggestion wasn't helpful..."
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => onReject(RejectionReason.OTHER)}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  variant="contained"
                  color="primary"
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Box>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
